import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { brl, formatDate, statusLabel, statusColor } from "@/lib/format";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, ShoppingBag, Users, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const StatCard = ({ icon: Icon, label, value, hint, color = "#C9708B", testid }) => (
  <div className="bg-white rounded-2xl p-5 border border-[#F9E1E8]" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-xs text-[#8C7A80] uppercase tracking-wider">{label}</p>
      <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{background: color+"22", color}}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="font-serif text-2xl font-bold text-[#4A3B40] mt-3">{value}</p>
    {hint && <p className="text-xs text-[#8C7A80] mt-1">{hint}</p>}
  </div>
);

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-[#8C7A80]">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl text-[#4A3B40]">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="Vendas (mês)" value={brl(s.revenue_month)} hint={`${s.orders_month} pedidos`} testid="stat-revenue-month" />
        <StatCard icon={ShoppingBag} label="Pedidos hoje" value={s.orders_today} hint={`Receita: ${brl(s.revenue_today)}`} color="#D4B483" testid="stat-orders-today" />
        <StatCard icon={TrendingUp} label="Ticket médio" value={brl(s.avg_ticket)} color="#7DBE9E" testid="stat-ticket" />
        <StatCard icon={Users} label="Clientes" value={s.customers} hint={`${s.pending_orders} pedidos pendentes`} color="#8C7A80" testid="stat-customers" />
      </div>

      {s.low_stock > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-800">{s.low_stock} produto(s) com estoque baixo (≤5 unidades). <Link to="/admin/produtos" className="font-semibold underline">Ver produtos</Link></p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#F9E1E8] p-5">
          <h3 className="font-serif text-xl text-[#4A3B40] mb-4">Vendas últimos 7 dias</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={s.sales_by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F9E1E8" />
              <XAxis dataKey="date" stroke="#8C7A80" tick={{fontSize:12}} />
              <YAxis stroke="#8C7A80" tick={{fontSize:12}} />
              <Tooltip contentStyle={{background:"#fff", border:"1px solid #F9E1E8", borderRadius:12}} formatter={(v,name)=> name==="total"? [brl(v), "Receita"] : [v, "Pedidos"]} />
              <Line type="monotone" dataKey="total" stroke="#C9708B" strokeWidth={2.5} dot={{r:4, fill:"#C9708B"}} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-[#F9E1E8] p-5">
          <h3 className="font-serif text-xl text-[#4A3B40] mb-4">Top produtos</h3>
          <div className="space-y-2">
            {s.top_products.slice(0,6).map((p) => (
              <div key={p.id} className="flex items-center gap-3">
                <img src={(p.images||[])[0] || ""} className="w-10 h-10 rounded-lg object-cover bg-[#F9E1E8]" alt="" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#4A3B40] line-clamp-1">{p.name}</p>
                  <p className="text-xs text-[#8C7A80]">{p.sales_count || 0} vendas</p>
                </div>
                <span className="text-sm font-bold text-[#C9708B]">{brl(p.price)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#F9E1E8] p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-[#4A3B40]">Pedidos recentes</h3>
          <Link to="/admin/pedidos" className="text-sm text-[#C9708B] font-semibold">Ver todos →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[#8C7A80] uppercase border-b border-[#F9E1E8]">
                <th className="pb-2">Pedido</th><th className="pb-2">Cliente</th><th className="pb-2">Data</th><th className="pb-2">Status</th><th className="pb-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {s.recent_orders.map((o) => (
                <tr key={o.id} className="border-b border-[#F9E1E8] last:border-0">
                  <td className="py-3"><Link to={`/admin/pedidos`} className="text-[#C9708B] font-semibold">{o.order_number}</Link></td>
                  <td>{o.customer_name}</td>
                  <td className="text-[#8C7A80]">{formatDate(o.created_at)}</td>
                  <td><span className={`text-xs px-2 py-1 rounded-full border ${statusColor(o.status)}`}>{statusLabel(o.status)}</span></td>
                  <td className="text-right font-semibold">{brl(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
